import { useMemo } from "react";
import { useHttp } from "../http/useHttp";
import { useAxiosQuery, UseAxiosQueryOptions } from "../http/useAxiosQuery";
import { useDefaultError } from "@synota-io/synota-shared-ui";
import { paths } from "../generated-types";

type InvoicesOperation = paths["/dashboard/contracts/{contract_shared_uuid}/invoices"]["post"];
type UseInvoicesResponse = InvoicesOperation["responses"][200]["content"]["application/json"];
type UseInvoicesRequestBody = InvoicesOperation["requestBody"]["content"]["application/json"];
type InvoicesPath = InvoicesOperation["parameters"]["path"];

export type IInvoice = UseInvoicesResponse["items"][number];
export type UseInvoicesInput = UseInvoicesRequestBody & {
  contract_uuid: InvoicesPath["contract_shared_uuid"];
};

export const useInvoices = (
  input: UseInvoicesInput = { contract_uuid: "" },
  options: Omit<UseAxiosQueryOptions<UseInvoicesResponse>, "queryKey" | "queryFn"> = {},
) => {
  const { client } = useHttp();

  const { status, isPending, isFetching, isLoading, data, error, refetch } =
    useAxiosQuery<UseInvoicesResponse>({
      queryKey: ["contract-invoices", input],
      queryFn: ({ signal }) =>
        client.post(`/contracts/${input.contract_uuid}/invoices`, input, { signal }),
      enabled: options.enabled !== false && !!input.contract_uuid,
      ...options,
    });

  const [invoices, hasNextPage] = useMemo(() => {
    if (!data) {
      return [[], false];
    }
    return [data.data.items, data.data.has_next_page];
  }, [data]);

  useDefaultError(error);

  return {
    invoices,
    hasNextPage,
    error,
    isLoading,
    isFetching,
    isPending,
    status,
    refetch,
  };
};

export type UseInvoicesReturn = ReturnType<typeof useInvoices>;
